import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'localization';
import { Navigate, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useGateValue } from '@statsig/react-bindings';
import { useAnalytics } from '@center-technologies/analytics';
import {
  LoaderMolecule,
  MarketplaceHomeOrganism,
  ChannelDetailOrganism,
  ChannelManagePageOrganism,
  ApproachDetailPageOrganism,
  TemplateDetailPageOrganism,
  PublisherIndexOrganism,
  PublisherCreateDialogOrganism,
  PublisherDetailOrganism,
  PublisherManageOrganism,
  PublicMarketplaceShell,
  usePublisherManage,
  useApiClientsOrNull,
  useUseCases,
  useSchemaTypeCacheReset,
  useMetaspaceEvents,
  recordRecentSphere,
  getChannelByPublisherSlug,
  ViewStateProvider,
} from 'design-system/my-lib';
import { SessionDataProvider } from './context/SessionDataContext';
import { useAppInitialization } from './hooks/useAppInitialization';
import OrganizationProvisioningScreen from './pages/OrganizationProvisioningScreen';
import NoOrganizationScreen from './pages/NoOrganizationScreen';
import OrganizationNotInEnvironmentScreen from './pages/OrganizationNotInEnvironmentScreen';
import { signInForAction } from './utils/signInHandoff';
import SphereRedirector from './routing/sphereRedirector';
import MetaspacePage from './templates/MetaspacePage/MetaspacePage';
import TreeMapPage from './templates/TreeMapPage/TreeMapPage';
import NotificationsPage from './templates/NotificationsPage/NotificationsPage';
import TimelinePage from './templates/TimelinePage/TimelinePage';
import ComposeTargetPage from './templates/ComposePage/ComposeTargetPage';
import ComposeCompositionPage from './templates/ComposePage/ComposeCompositionPage';
import ComposeGalleryPage from './templates/ComposePage/ComposeGalleryPage';
import LegacyComposeRedirect from './templates/Studio/LegacyComposeRedirect';
import LegacyContextRedirect from './templates/Studio/LegacyContextRedirect';
import StudioShell from './templates/Studio/StudioShell';
import StudioModeHome from './templates/Studio/StudioModeHome';
import StudioBuilderPlaceholder from './templates/Studio/StudioBuilderPlaceholder';
import StudioTypeGalleryPage from './templates/Studio/StudioTypeGalleryPage';
import SphereTypeEditorPage from './templates/Studio/SphereTypeEditorPage';
import ArcEditorPage from './templates/Studio/ArcEditorPage';
import StudioContextGalleryPage from './templates/Studio/StudioContextGalleryPage';
import StudioContextEditorPage from './templates/Studio/StudioContextEditorPage';
import { STUDIO_BUILDERS } from './templates/Studio/builders';
import Header from '../app/templates/Header/Header';
import {
  RequireMetasphereAdmin,
  MetasphereAdminShell,
  ADMIN_SECTIONS,
  DEFAULT_ADMIN_SECTION_ID,
} from './templates/MetasphereAdmin';
import {
  RequireOrgAdmin,
  OrgSettingsShell,
  SETTINGS_SECTIONS,
  SettingsRedirector,
} from './templates/OrgSettings';
import type { RootState } from '@center-pwa/store/store';
import '../assets/common.scss';
import './app.scss';

const MarketplaceChannelDetailRoute: React.FC = () => {
  const { channelId } = useParams<{ channelId: string }>();
  const navigate = useNavigate();
  // Anchor "Nearby" to the sphere the navbar/address bar currently shows. On
  // marketplace routes the Header is rendered with `sphere={currentSphere}`
  // (state.userProfile.userSphere), so the anchor must read that same slice to
  // stay consistent with what the user sees; fall back to the last-centered
  // sphere only if the user sphere hasn't loaded yet.
  const nearbyAnchorSphereId = useSelector(
    (state: RootState) =>
      state.userProfile?.userSphere?.id ?? state.sphere?.sphereId ?? undefined
  );

  if (!channelId) return <Navigate to="/marketplace" replace />;

  return (
    <ChannelDetailOrganism
      channelId={channelId}
      isSubscribed={false}
      isMandatory={false}
      onBack={() => navigate('/marketplace')}
      onSubscriptionChange={() => {
        /* no-op: parent list refetches on remount */
      }}
      onManage={(id) => navigate(`/marketplace/channels/${id}/manage`)}
      nearbyAnchorSphereId={nearbyAnchorSphereId}
    />
  );
};

const MarketplaceApproachDetailRoute: React.FC = () => {
  const { systemName } = useParams<{ systemName: string }>();
  const sphereId = useSelector(
    (state: RootState) => state.sphere?.sphereId
  ) ?? undefined;

  if (!systemName) return <Navigate to="/marketplace" replace />;

  return <ApproachDetailPageOrganism systemName={systemName} currentSphereId={sphereId} />;
};

const MarketplaceTemplateItemDetailRoute: React.FC = () => {
  const { channelId, itemId } = useParams<{ channelId: string; itemId: string }>();

  if (!channelId || !itemId) return <Navigate to="/marketplace" replace />;

  return <TemplateDetailPageOrganism channelId={channelId} itemId={itemId} />;
};

const MarketplaceHomeRoute: React.FC = () => {
  const { useCases, loading: useCasesLoading, error: useCasesError } = useUseCases();

  return (
    <MarketplaceHomeOrganism
      useCases={useCases}
      useCasesLoading={useCasesLoading}
      useCasesError={useCasesError}
    />
  );
};

const MarketplacePublisherIndexRoute: React.FC = () => {
  const navigate = useNavigate();
  const [showCreate, setShowCreate] = useState(false);
  return (
    <>
      <PublisherIndexOrganism
        onBack={() => navigate('/marketplace')}
        onCreatePublisher={() => setShowCreate(true)}
      />
      <PublisherCreateDialogOrganism
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={(publisher) => {
          setShowCreate(false);
          navigate(`/marketplace/publishers/${encodeURIComponent(publisher.slug)}`);
        }}
      />
    </>
  );
};

const MarketplacePrettyChannelRoute: React.FC = () => {
  const { publisherSlug, channelSystemName } = useParams<{ publisherSlug: string; channelSystemName: string }>();
  const navigate = useNavigate();
  const apiClients = useApiClientsOrNull();
  const { t } = useTranslation();
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!publisherSlug || !channelSystemName || !apiClients?.schemaClient) return;
    getChannelByPublisherSlug(apiClients.schemaClient, publisherSlug, channelSystemName)
      .then(({ channel }) => navigate(`/marketplace/channels/${encodeURIComponent(channel.id)}`, { replace: true }))
      .catch(() => setNotFound(true));
  }, [publisherSlug, channelSystemName, apiClients, navigate]);

  if (notFound) {
    return (
      <div className="a2ui-marketplace__not-found">
        <p className="a2ui-marketplace__error-message">{t('marketplace.channels.notFound')}</p>
        <button type="button" className="a2ui-marketplace__retry-btn" onClick={() => navigate('/marketplace/publishers')}>
          {t('marketplace.publishers.index.back')}
        </button>
      </div>
    );
  }

  return null;
};

const MarketplacePublisherDetailRoute: React.FC = () => {
  const { publisherSlug } = useParams<{ publisherSlug: string }>();
  const navigate = useNavigate();
  const { submit, approve, reject } = usePublisherManage();

  if (!publisherSlug) return <Navigate to="/marketplace/publishers" replace />;

  return (
    <PublisherDetailOrganism
      slug={publisherSlug}
      onBack={() => navigate('/marketplace/publishers')}
      onChannelClick={(channelId) => navigate(`/marketplace/channels/${channelId}`)}
      onSubmit={submit}
      onApprove={approve}
      onReject={reject}
    />
  );
};

const MarketplacePublisherManageRoute: React.FC = () => {
  const { publisherSlug } = useParams<{ publisherSlug: string }>();
  const navigate = useNavigate();

  if (!publisherSlug) return <Navigate to="/marketplace/publishers" replace />;

  return (
    <PublisherManageOrganism
      slug={publisherSlug}
      onBack={() => navigate(`/marketplace/publishers/${publisherSlug}`)}
      onChannelClick={(channelId) => navigate(`/marketplace/channels/${channelId}`)}
    />
  );
};

const MarketplaceChannelManageRoute: React.FC = () => {
  const { channelId } = useParams<{ channelId: string }>();
  const navigate = useNavigate();
  const { useCases, loading: useCasesLoading, error: useCasesError } = useUseCases();

  if (!channelId) return <Navigate to="/marketplace" replace />;

  return (
    <ChannelManagePageOrganism
      channelId={channelId}
      onBack={() => navigate(`/marketplace/channels/${channelId}`)}
      useCases={useCases}
      useCasesLoading={useCasesLoading}
      useCasesError={useCasesError}
    />
  );
};

// Records a "recent sphere" touch whenever the user opens a sphere editor or
// creates/updates a sphere — the spec's recent-touch triggers (picker selection
// is handled inside the picker itself). Centralised on the metaspace event bus,
// which AtlasTree emits SPHERE_CREATED/SPHERE_UPDATED into and Header emits
// OPEN_SPHERE_EDITOR into. Renders nothing.
const RECENT_TOUCH_EVENT_TYPES = ['OPEN_SPHERE_EDITOR', 'SPHERE_CREATED', 'SPHERE_UPDATED'];

const RecentSphereTracker: React.FC = () => {
  const apiClients = useApiClientsOrNull();
  const handleEvent = React.useCallback(
    (event: { payload?: { sphereId?: string } }) => {
      const sphereId = event?.payload?.sphereId;
      const searchClient = apiClients?.searchClient;
      if (sphereId && searchClient) {
        recordRecentSphere(searchClient, sphereId).catch(() => undefined);
      }
    },
    [apiClients]
  );
  useMetaspaceEvents({
    subscriberId: 'recent-sphere-tracker',
    eventTypes: RECENT_TOUCH_EVENT_TYPES,
    onEvent: handleEvent,
  });
  return null;
};

export function App() {
  const { t } = useTranslation();
  const [metaspaceId, setMetaspaceId] = useState<string>('');
  const initialization = useAppInitialization();
  const isPublicMarketplaceEnabled = useGateValue('feature_public_marketplace_enabled');
  const logAnalyticsEvent = useAnalytics();
  const navigate = useNavigate();
  const location = useLocation();

  // Initialize theme detection

  // Flush the schema-type demand cache + coalescer on org/identity switch.
  useSchemaTypeCacheReset();

  const defaultLocale = process.env.REACT_APP_I18N_LOCALE || 'en-US';
  const userLocale = useSelector((s: RootState) => s.userProfile.locale ?? defaultLocale);

  const currentSphere = useSelector((state: RootState) => state.userProfile?.userSphere);

  // JIRA collector setup
  useEffect(() => {
    if (process.env.REACT_APP_ENABLE_JIRA_COLLECTOR === 'true') {
      const script = document.createElement('script');
      script.src = process.env.REACT_APP_JIRA_COLLECTOR_URL ?? '';
      document.body.appendChild(script);
    }
  }, []);

  // Local-only: REACT_APP_BRANCH_NAME is baked in solely for `NODE_ENV=local` builds
  // (webpack.config.js), so this never runs outside a developer's own worktree.
  useEffect(() => {
    if (process.env.REACT_APP_BRANCH_NAME) {
      document.title = `Center [${process.env.REACT_APP_BRANCH_NAME}]`;
    }
  }, []);

  if (initialization.isProvisioningOrganization) {
    return (
      <OrganizationProvisioningScreen
        status={initialization.provisioningStatus}
        progress={initialization.provisioningProgress}
        runId={initialization.provisioningRunId}
        organizationName={initialization.provisioningOrgName}
        pollError={initialization.provisioningPollError}
        unreachable={initialization.provisioningUnreachable}
        notFound={initialization.provisioningNotFound}
        onRetry={initialization.retryProvisioningStatus}
        onContinue={initialization.continueAfterProvisioning}
      />
    );
  }

  if (initialization.isNotInEnvironment) {
    return (
      <OrganizationNotInEnvironmentScreen
        hasOtherOrganizations={initialization.hasOtherOrganizations}
        onChooseAnotherOrganization={initialization.chooseAnotherOrganization}
      />
    );
  }

  // The no-organization gate stays mounted while it re-checks, so the person keeps their place
  // (and their focus) instead of being swapped for the loader and back.
  if (initialization.hasNoOrganization || initialization.isCheckingAgain) {
    return (
      <NoOrganizationScreen
        waitlistStatus={initialization.waitlistStatus}
        verificationRequired={initialization.waitlistVerificationRequired}
        email={initialization.identityEmail}
        checking={initialization.isCheckingAgain}
        onCheckAgain={initialization.checkAgain}
      />
    );
  }

  // Loading states
  if (initialization.isLoading) {
    // Narrate what the app is actually doing rather than showing one static caption.
    // Each phase gets its own caption and a share of the progress bar, so a slow step
    // reads as progress rather than as a hang.
    const phaseCaption = t(`common.loader.phase.${initialization.phase}`, {
      defaultValue: t('common.loader.caption'),
    });
    const phaseProgress: Record<string, number> = {
      idle: 5,
      authenticating: 20,
      bootstrapping: 30,
      'loading-profile': 45,
      'loading-organization': 70,
      'loading-localizations': 90,
      ready: 100,
    };

    return (
      <div className="common-loader">
        <LoaderMolecule
          variant="inline"
          caption={phaseCaption}
          progress={phaseProgress[initialization.phase] ?? 5}
        />
        {/* Optional: Show current phase for debugging */}
        {process.env.NODE_ENV === 'development' && (
          <div className="app-init-phase">{initialization.phase.replace('-', ' ')}...</div>
        )}
      </div>
    );
  }

  if (initialization.hasError) {
    return (
      <div className="error-container">
        <p>{t('appInit.initializationFailed', { error: initialization.error })}</p>
        {/* An error trying again can fix gets Try again; any other still offers a page refresh. */}
        {initialization.errorRetryable ? (
          <button type="button" onClick={initialization.retryInitialization}>
            {t('appInit.tryAgain')}
          </button>
        ) : (
          <button type="button" onClick={() => globalThis.location.reload()}>
            {t('appInit.refreshPage')}
          </button>
        )}
        {process.env.NODE_ENV === 'development' && (
          <details className="app-error-debug">
            <summary>Debug Info</summary>
            <pre>{JSON.stringify(initialization.debug, null, 2)}</pre>
          </details>
        )}
      </div>
    );
  }

  // Exactly one of three shells renders: the public marketplace (gate on +
  // unauthenticated), the authenticated app, or the loader while we resolve.
  const showPublicMarketplace = isPublicMarketplaceEnabled && !initialization.userId;

  return (
    <div className="app-root">
      {initialization.warning && (
        <div className="app-warning-banner">
          <div className="app-warning-content">
            <span className="app-warning-icon">⚠️</span>
            <span>{initialization.warning}</span>
          </div>
          <button
            onClick={() => globalThis.location.reload()}
            className="app-warning-retry"
          >
            Retry
          </button>
        </div>
      )}
      <SessionDataProvider>
        {/* Gate on + unauthenticated: serve public marketplace shell before the userId guard.
            The server-side unprotectedRoutes allowlist is the security boundary;
            this client-side gate is a rollout/kill-switch, not a permission check. */}
        {showPublicMarketplace && (
          <Routes>
            <Route
              path="/marketplace/*"
              element={
                <PublicMarketplaceShell
                  onPageViewed={() =>
                    logAnalyticsEvent('public_marketplace_viewed', undefined, { entry: 'grid' })
                  }
                  onSignIn={() => {
                    navigate(signInForAction(`${location.pathname}${location.search}`));
                  }}
                  onSignInForAction={(returnTo, action) => {
                    logAnalyticsEvent('public_sign_in_prompted', undefined, {
                      action,
                      systemName: returnTo.split('/').pop() ?? '',
                    });
                    navigate(signInForAction(returnTo));
                  }}
                  onListingOpened={(type, identifier) =>
                    logAnalyticsEvent('public_listing_opened', undefined, { type, identifier })
                  }
                />
              }
            />
            <Route path="*" element={<div className="common-loader"><LoaderMolecule /></div>} />
          </Routes>
        )}

        {/* The session layer of the four-layer view-config resolver
            (`defaults -> view -> user -> session`) lives HERE, above the router
            outlet, and not inside any visualization.

            It has to outlive any one of them: a user who rearranges a board in
            the TreeMap and switches to Atlas on the same context expects the
            unsaved tweak to still be there (EAINT-780,
            `specs/view-personalization-overlay.md` § The session layer).
            `ViewStateBoundary` inside each organism defers to this provider and
            self-provides only when standing alone — Storybook, tests, embeds.
            Until this mount existed the boundary ALWAYS self-provided, so the
            session layer's lifetime was exactly the visualization's and every
            unsaved tweak was discarded on navigation, silently: `useViewState`
            resolves against the nearest provider and cannot tell it found the
            wrong one.

            Mounting at the router rather than per-route is what makes the
            lifetime SPA-wide. It does not widen SCOPE — session layers key on
            `(contextRef, viewSystemName)`, so there is no global bucket for one
            context to bleed into another, and leaving a context still clears
            its whole subtree via `resetSessionForContext`. */}
        {!showPublicMarketplace && initialization.userId && (
          <ViewStateProvider>
          <div className="main-container">
            <RecentSphereTracker />
            <Routes>
              <Route path="/" element={<SphereRedirector />} />
              <Route path="/sphere" element={<SphereRedirector />} />
              <Route path="/sphere/:sphereId" element={<SphereRedirector />} />
              <Route path="/sphere/:sphereId/timeline" element={<TimelinePage />} />
              <Route path="/sphere/:sphereId/metaspace/:metaspaceId" element={<MetaspacePage onMetaspaceIdChange={setMetaspaceId} />} />
              <Route path="/sphere/:sphereId/tree-map/:contextSystemName" element={<TreeMapPage />} />
              <Route path="/notifications" element={<NotificationsPage />} />
              {/* Studio (EAINT-858) — the canonical creation address hosting six builders. The
                  Space and Context builders are fully functional (the Space Designer re-parented
                  unchanged from `/compose*`; the context library + editor re-parented unchanged
                  from `/contexts*`, EAINT-862); approach/arc/aspect (EAINT-873) are `browse`-status
                  — a read-only gallery + drawer via `StudioTypeGalleryPage`, no editor yet; the
                  remaining builder mounts the real Studio frame via `StudioBuilderPlaceholder`
                  with an explicit not-yet-built body. */}
              <Route path="/studio" element={<StudioShell />}>
                <Route index element={<StudioModeHome />} />
                <Route path="space" element={<ComposeGalleryPage />} />
                <Route path="space/:id" element={<ComposeTargetPage />} />
                <Route path="composition/:name" element={<ComposeCompositionPage />} />
                <Route path="context" element={<StudioContextGalleryPage />} />
                <Route path="context/new" element={<StudioContextEditorPage />} />
                <Route path="context/:systemName" element={<StudioContextEditorPage />} />
                {/* Sphere Type Editor (EAINT-892) — addressed by schema_id, outside any
                    builder's own segment; `approach`'s aliasSegments (builders.tsx) keeps the
                    shell's nav highlight and breadcrumb pointed at it while this is open. */}
                <Route path="types/:schemaId" element={<SphereTypeEditorPage />} />
                {/* Arc Editor Canvas (EAINT-951) — addressed by systemName/version, outside the
                    Arc builder's own `browse` segment, same convention as the Sphere Type
                    Editor above. `arc/new` has no systemName yet — `ArcEditorPage` resolves it
                    to a blank draft. */}
                <Route path="arc/new" element={<ArcEditorPage />} />
                <Route path="arc/:systemName/:version" element={<ArcEditorPage />} />
                {STUDIO_BUILDERS.filter((builder) => builder.status === 'browse').map((builder) => (
                  <Route
                    key={builder.id}
                    path={builder.segment}
                    element={<StudioTypeGalleryPage builder={builder} />}
                  />
                ))}
                {STUDIO_BUILDERS.filter((builder) => builder.status === 'scaffolded').map((builder) => (
                  <Route
                    key={builder.id}
                    path={builder.segment}
                    element={<StudioBuilderPlaceholder builder={builder} />}
                  />
                ))}
                {/* Unknown builder segment redirects to the Studio home rather than a blank
                    shell (spec Edge Cases) — "." is relative to this /studio route, i.e. the
                    index above, not a hop back into a specific builder. */}
                <Route path="*" element={<Navigate to="." replace />} />
              </Route>
              {/* Legacy `/compose*` URLs redirect to their `/studio*` equivalent, preserving the
                  query string — approach-scoped composing (`?scope=...`) and agent focus
                  (`?agent=1`) live in query params, not path segments. */}
              <Route path="/compose" element={<LegacyComposeRedirect toPathFromParams={() => '/studio/space'} />} />
              <Route
                path="/compose/space/:id"
                element={<LegacyComposeRedirect toPathFromParams={(params) => `/studio/space/${params.id}`} />}
              />
              <Route
                path="/compose/composition/:name"
                element={<LegacyComposeRedirect toPathFromParams={(params) => `/studio/composition/${params.name}`} />}
              />
              {/* Legacy `/contexts*` URLs redirect to their `/studio/context*` equivalent on
                  desktop, preserving the query string; below `StudioShell`'s own
                  `(max-width: 1023px)` breakpoint `/contexts` and `/contexts/:sn/edit` keep
                  working as a read-and-clone alias (EAINT-862 SC3). */}
              <Route
                path="/contexts"
                element={<LegacyContextRedirect toPathFromParams={() => '/studio/context'} mobileAlias="list" />}
              />
              <Route
                path="/contexts/new"
                element={<LegacyContextRedirect toPathFromParams={() => '/studio/context/new'} />}
              />
              <Route
                path="/contexts/new/blank"
                element={<LegacyContextRedirect toPathFromParams={() => '/studio/context/new'} />}
              />
              <Route
                path="/contexts/:systemName/edit"
                element={
                  <LegacyContextRedirect
                    toPathFromParams={(params) => `/studio/context/${params.systemName}`}
                    mobileAlias="editor"
                  />
                }
              />
              <Route path="/marketplace" element={<><Header sphereId={initialization.userId} defaultLanguage={userLocale} metaspaceId={metaspaceId} sphere={currentSphere} /><MarketplaceHomeRoute /></>} />
              <Route path="/marketplace/channels/:channelId" element={<><Header sphereId={initialization.userId} defaultLanguage={userLocale} metaspaceId={metaspaceId} sphere={currentSphere} /><MarketplaceChannelDetailRoute /></>} />
              <Route path="/marketplace/channels/:channelId/manage" element={<><Header sphereId={initialization.userId} defaultLanguage={userLocale} metaspaceId={metaspaceId} sphere={currentSphere} /><MarketplaceChannelManageRoute /></>} />
              <Route path="/marketplace/channels/:channelId/items/:itemId" element={<><Header sphereId={initialization.userId} defaultLanguage={userLocale} metaspaceId={metaspaceId} sphere={currentSphere} /><MarketplaceTemplateItemDetailRoute /></>} />
              <Route path="/marketplace/approaches/:systemName" element={<><Header sphereId={initialization.userId} defaultLanguage={userLocale} metaspaceId={metaspaceId} sphere={currentSphere} /><MarketplaceApproachDetailRoute /></>} />
              <Route path="/marketplace/publishers" element={<><Header sphereId={initialization.userId} defaultLanguage={userLocale} metaspaceId={metaspaceId} sphere={currentSphere} /><MarketplacePublisherIndexRoute /></>} />
              <Route path="/marketplace/publishers/:publisherSlug" element={<><Header sphereId={initialization.userId} defaultLanguage={userLocale} metaspaceId={metaspaceId} sphere={currentSphere} /><MarketplacePublisherDetailRoute /></>} />
              <Route path="/marketplace/publishers/:publisherSlug/manage" element={<><Header sphereId={initialization.userId} defaultLanguage={userLocale} metaspaceId={metaspaceId} sphere={currentSphere} /><MarketplacePublisherManageRoute /></>} />
              <Route path="/marketplace/:publisherSlug/:channelSystemName" element={<><Header sphereId={initialization.userId} defaultLanguage={userLocale} metaspaceId={metaspaceId} sphere={currentSphere} /><MarketplacePrettyChannelRoute /></>} />
              <Route
                path="/admin/metasphere"
                element={
                  <RequireMetasphereAdmin>
                    <MetasphereAdminShell />
                  </RequireMetasphereAdmin>
                }
              >
                <Route
                  index
                  element={<Navigate to={DEFAULT_ADMIN_SECTION_ID} replace />}
                />
                {ADMIN_SECTIONS.map((section) => (
                  <Route key={section.id} path={section.id}>
                    <Route index element={section.listElement} />
                    {section.detailPath && (
                      <Route
                        path={section.detailPath}
                        element={section.detailElement}
                      />
                    )}
                  </Route>
                ))}
              </Route>
              {/* Org Settings — gated by REACT_APP_ENABLE_ORG_SETTINGS.
                  Set in environments/.env.local only; off everywhere else
                  until the console is ready for broader exposure. When the
                  flag is unset/false, no /settings or /:orgId/settings routes
                  exist and a 404-style fallthrough applies. */}
              {process.env.REACT_APP_ENABLE_ORG_SETTINGS === 'true' && (
                <>
                  <Route path="/settings" element={<SettingsRedirector />} />
                  <Route
                    path="/:orgId/settings"
                    element={
                      <RequireOrgAdmin>
                        <OrgSettingsShell />
                      </RequireOrgAdmin>
                    }
                  >
                    <Route
                      index
                      element={<Navigate to={SETTINGS_SECTIONS[0].id} replace />}
                    />
                    {SETTINGS_SECTIONS.map((section) => (
                      <Route key={section.id} path={section.id}>
                        <Route index element={section.listElement} />
                      </Route>
                    ))}
                  </Route>
                </>
              )}
            </Routes>
          </div>
          </ViewStateProvider>
        )}

        {!showPublicMarketplace && !initialization.userId && (
          <div className="common-loader">
            <LoaderMolecule variant="inline" caption={t('common.loader.caption')} />
          </div>
        )}
      </SessionDataProvider>
    </div>
  );
}

export default App;
